import React, { useState, useEffect } from 'react';
import { Droplet, TrendingUp, TrendingDown, ArrowRight, RefreshCw, Info } from 'lucide-react';

// Tipos para los datos de combustibles
interface FuelPrice {
  type: string;
  price: number;
  unit: string;
  variation: number;
  trend: 'up' | 'down' | 'stable';
}

// Componente principal
const FuelPricesTable: React.FC = () => {
  const [fuelPrices, setFuelPrices] = useState<FuelPrice[]>([]);
  const [location, setLocation] = useState<string>('México');
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState<boolean>(false);

  // Función para obtener los precios actuales (simulados)
  useEffect(() => {
    // Simulamos la carga de datos
    setLoading(true);
    
    // Obtenemos la fecha actual para la última actualización
    const now = new Date();
    const formattedDate = now.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const formattedTime = now.toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    setLastUpdated(`${formattedDate} a las ${formattedTime}`);
    
    // Simulamos datos de precios de combustible
    // En una aplicación real, estos datos vendrían de una API
    setTimeout(() => {
      const mockPrices: FuelPrice[] = [
        {
          type: 'Gasolina Premium',
          price: 25.89,
          unit: 'litro',
          variation: 0.15,
          trend: 'up'
        },
        {
          type: 'Gasolina Regular',
          price: 23.45,
          unit: 'litro',
          variation: 0.08,
          trend: 'up'
        },
        {
          type: 'Diesel',
          price: 24.67,
          unit: 'litro',
          variation: -0.12,
          trend: 'down'
        },
        {
          type: 'Gas LP',
          price: 12.35,
          unit: 'litro',
          variation: 0.00,
          trend: 'stable'
        }
      ];
      
      setFuelPrices(mockPrices);
      setLoading(false);
    }, 1000);
  }, []);

  // Función para refrescar los datos
  const handleRefresh = () => {
    // En una aplicación real, esto llamaría a la API nuevamente
    setLoading(true);
    
    // Actualizamos la hora
    const now = new Date();
    const formattedDate = now.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const formattedTime = now.toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    setLastUpdated(`${formattedDate} a las ${formattedTime}`);
    
    // Simulamos pequeñas variaciones en los precios
    setTimeout(() => {
      setFuelPrices(prevPrices => 
        prevPrices.map(fuel => {
          const randomVariation = (Math.random() * 0.4 - 0.2).toFixed(2);
          const newVariation = parseFloat(randomVariation);
          const newPrice = parseFloat((fuel.price + newVariation).toFixed(2));
          
          return {
            ...fuel,
            price: newPrice,
            variation: newVariation,
            trend: newVariation > 0 ? 'up' : newVariation < 0 ? 'down' : 'stable'
          };
        })
      );
      setLoading(false);
    }, 1000);
  };

  // Función para renderizar el icono de tendencia
  const renderTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <TrendingUp size={14} className="text-red-500" />;
      case 'down':
        return <TrendingDown size={14} className="text-green-500" />;
      default:
        return <ArrowRight size={14} className="text-gray-500" />;
    }
  };

  // Función para formatear la variación con signo
  const formatVariation = (variation: number): string => {
    return variation > 0 
      ? `+${variation.toFixed(2)}` 
      : variation.toFixed(2);
  };

  // Función para obtener el color de la variación
  const getVariationColor = (variation: number): string => {
    return variation > 0 
      ? 'text-red-500' 
      : variation < 0 
        ? 'text-green-500' 
        : 'text-gray-500';
  };

  // Función para obtener el color de fondo según el tipo de combustible
  const getFuelColor = (fuelType: string): string => {
    switch (fuelType) {
      case 'Gasolina Premium':
        return 'bg-red-50 border-red-200';
      case 'Gasolina Regular':
        return 'bg-green-50 border-green-200';
      case 'Diesel':
        return 'bg-yellow-50 border-yellow-200';
      case 'Gas LP':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  // Toggle collapsed state
  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-3 bg-gradient-to-r from-primary to-primary-dark text-white cursor-pointer" onClick={toggleCollapsed}>
        <div className="flex justify-between items-center">
          <h2 className="text-base font-bold flex items-center">
            <Droplet className="mr-1.5 flex-shrink-0" size={16} />
            Precios de Combustibles
          </h2>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setShowInfo(!showInfo);
            }}
            className="p-1 rounded-full hover:bg-primary-dark transition-colors"
          >
            <Info size={14} />
          </button>
        </div>
      </div>

      {showInfo && !collapsed && (
        <div className="p-2 bg-blue-50 border-b border-blue-100 text-xs text-blue-800">
          <p>Esta tabla muestra los precios promedio de combustibles en {location}. Los precios pueden variar según la ubicación.</p>
        </div>
      )}

      {!collapsed && (
        <>
          {loading ? (
            <div className="p-4 flex justify-center items-center">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Combustible
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Precio (MXN)
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Variación
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {fuelPrices.map((fuel, index) => (
                    <tr key={index} className={`${getFuelColor(fuel.type)} hover:bg-gray-50`}>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-900">{fuel.type}</div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-bold text-gray-900">
                          ${fuel.price.toFixed(2)}/{fuel.unit}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className={`text-xs font-medium ${getVariationColor(fuel.variation)} flex items-center`}>
                          {renderTrendIcon(fuel.trend)}
                          <span className="ml-1">{formatVariation(fuel.variation)}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="p-2 bg-gray-50 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <div className="text-xs text-gray-500">
                <p className="text-xs">Actualizado: {lastUpdated.split(" a las")[0]}</p>
              </div>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRefresh();
                }}
                className="flex items-center text-primary hover:text-primary-dark text-xs"
              >
                <RefreshCw size={12} className="mr-1" />
                Actualizar
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FuelPricesTable;